import { useEffect, useMemo } from 'react'

import { useGetPropertyDetail } from 'dataQueries'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import type { CartItemId, ConfigurationFlowId } from 'models/purchaseFlowRevamp'
import { PropertyType, type RealEstatePropertyDTO } from 'models/property/realEstatePropertyDTO'

import { usePfStore } from '../store'
import { makeConfigKey } from '../store/slices/productConfigSlice'
import { usePfEffectiveWorkspaceId } from '../entry/usePfEffectiveWorkspaceId'
import { resolveTabsFromCartAndCatalogue } from '../steps/configuration/resolveTabs'

const SQFT_IN_SQM = 0.09290304

/** Which field of a configuration takes the property's area, and whether only a residential property's. */
const AREA_FIELDS: Partial<Record<ConfigurationFlowId, { field: string, residentialOnly: boolean }>> = {
  measurement: { field: 'surfaceArea', residentialOnly: false },
  consumption_certificate: { field: 'totalLivingArea', residentialOnly: true },
  energy_certificate_of_need: { field: 'totalLivingArea', residentialOnly: true },
}

/** The property's area in m², rounded to whole metres; null when it has none. */
export const propertyAreaInSqm = (property: Pick<RealEstatePropertyDTO, 'surface'> | undefined): number | null => {
  const surface = property?.surface
  if (!surface || !(surface.value > 0)) return null
  return Math.round(surface.unit === 'SQFT' ? surface.value * SQFT_IN_SQM : surface.value)
}

/**
 * The configuration fields still to be filled from the property: those of the given tabs that take an area and have
 * never been stored, so the client's own value, including a cleared one, is never overwritten. A living area is not
 * filled for a commercial property.
 */
export const pendingAreaPrefills = (
  tabs: ReadonlyArray<{ cartItemId: CartItemId, flowId: ConfigurationFlowId }>,
  productConfigs: Record<string, unknown>,
  propertyType: PropertyType | undefined
): Array<{ cartItemId: CartItemId, flowId: ConfigurationFlowId, field: string }> =>
  tabs.flatMap(({ cartItemId, flowId }) => {
    const target = AREA_FIELDS[flowId]
    if (!target || (target.residentialOnly && propertyType === PropertyType.COMMERCIAL)) return []
    const stored = productConfigs[makeConfigKey(cartItemId, flowId)] as Record<string, unknown> | undefined
    return stored?.[target.field] === undefined ? [{ cartItemId, flowId, field: target.field }] : []
  })

/**
 * The floor-plan configurations that have no property type yet, which the property's own type then fills. A line with
 * no configuration at all is left alone while another floor plan has one: it takes that one's answers, type included,
 * and a configuration written first would stop it from doing so.
 */
export const pendingPropertyTypePrefills = (
  tabs: ReadonlyArray<{ cartItemId: CartItemId, flowId: ConfigurationFlowId }>,
  productConfigs: Record<string, unknown>
): CartItemId[] => {
  const floorPlans = tabs
    .filter(({ flowId }) => flowId === 'floor_plan_config')
    .map(({ cartItemId }) => ({ cartItemId, config: productConfigs[makeConfigKey(cartItemId, 'floor_plan_config')] as { selectedPropertyType?: unknown } | undefined }))
  const isAnyConfigured = floorPlans.some(({ config }) => config !== undefined)
  return floorPlans
    .filter(({ config }) => (config ? !config.selectedPropertyType : !isAnyConfigured))
    .map(({ cartItemId }) => cartItemId)
}

/**
 * Fills the area fields of the cart's configurations from the order's property, once per field, and the floor plan's
 * property type while it has none. Mounted at the flow
 * root so it runs as soon as a product is added, before its form mounts: the number fields read their value once.
 */
export function usePropertyAreaPrefills(): void {
  const items = usePfStore((s) => s.items)
  const catalogue = usePfCatalogue().data
  const allowFallbackFlowResolver = useFeatureFlag(FeatureFlag.ALLOW_PF_REVAMP_FALLBACK_FLOW_RESOLVER)
  const tabs = useMemo(
    () => catalogue ? resolveTabsFromCartAndCatalogue(items, catalogue.shellProducts, allowFallbackFlowResolver) : [],
    [items, catalogue, allowFallbackFlowResolver]
  )

  const propertyId = usePfStore((s) => s.property?.id ?? null)
  const workspaceId = usePfEffectiveWorkspaceId()
  // The client representation, as for the entry completeness check: an admin's own mimetype 406s here.
  const detail = useGetPropertyDetail(propertyId, workspaceId || null, true, { asClient: true })
  const area = propertyAreaInSqm(detail.data)
  const propertyType = detail.data?.propertyType

  useEffect(() => {
    if (area === null) return
    const state = usePfStore.getState()
    for (const { cartItemId, flowId, field } of pendingAreaPrefills(tabs, state.productConfigs, propertyType)) {
      const current = usePfStore.getState().productConfigs[makeConfigKey(cartItemId, flowId)] as Record<string, unknown> | undefined
      state.patchConfig(cartItemId, flowId, { ...current, [field]: area })
    }
  }, [tabs, area, propertyType])

  // Re-runs whenever a floor plan loses its choice too, as picking a template or starting from scratch resets it.
  const pendingPropertyTypes = usePfStore((s) => pendingPropertyTypePrefills(tabs, s.productConfigs).join(','))
  useEffect(() => {
    if (!propertyType || !pendingPropertyTypes) return
    const state = usePfStore.getState()
    for (const cartItemId of pendingPropertyTypePrefills(tabs, state.productConfigs)) {
      const current = usePfStore.getState().productConfigs[makeConfigKey(cartItemId, 'floor_plan_config')] as Record<string, unknown> | undefined
      state.patchConfig(cartItemId, 'floor_plan_config', { ...current, selectedPropertyType: propertyType })
    }
  }, [tabs, propertyType, pendingPropertyTypes])
}
